import { stopStream } from 'UTILS/stream';
import { DUMMY_LOGGER } from 'UTILS/constants';

export const MIN_DECIBELS = -95;
export const MAX_DECIBELS = -15;
export const SMOOTHING_TIME = 0.85;
export const SINE_WAVE_FFT_SIZE = 2048;
export const BARS_FFT_SIZE = 256;
export const MIC_DETECTION_THRESHOLD = 10;

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  } : null;
};

export const getInputSound = ({
  deviceId,
  timeoutDuration = 5000,
  logger = DUMMY_LOGGER,
}) => new Promise(async(resolve, reject) => {
  logger.log('VolumeMeter - getInputSound', { timeoutDuration, deviceId });

  const volumeMeter = createVolumeMeter({
    onStep: (dataArray) => {
      logger.log('VolumeMeter - Step', dataArray);
      if (dataArray.some((value) => value >= MIC_DETECTION_THRESHOLD)) {
        if (timeout) {
          clearTimeout(timeout);
        }
        volumeMeter.stop();
        resolve();
      }
    },
    logger,
  });
  const stream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId } });
  const audioTrack = stream?.getAudioTracks()?.[0];
  if (!audioTrack) {
    throw new Error('no_audio_track_available');
  }
  volumeMeter.start(stream);
  const timeout = setTimeout(() => {
    logger.log('VolumeMeter - Time out');
    volumeMeter.stop();
    reject(new Error('no_volume_timeout'));
  }, timeoutDuration);
});

export const createVolumeMeter = ({
  visualType = 'frequency_bars',
  minDecibels = MIN_DECIBELS,
  maxDecibels = MAX_DECIBELS,
  smoothingTime = SMOOTHING_TIME,
  canvasId,
  onStep = () => {},
  color = '#ffffff',
  logger = DUMMY_LOGGER,
}) => {
  logger.log('VolumeMeter - createVolumeMeter', { canvasId, color });
  const { r, g, b } = hexToRgb(color);
  let type = visualType;
  let shouldStop = false;
  let stream = null;
  const audioCtx = new (window.AudioContext || window.webkitAudioContext)();

  const analyser = audioCtx.createAnalyser();
  analyser.minDecibels = minDecibels;
  analyser.maxDecibels = maxDecibels;
  analyser.smoothingTimeConstant = smoothingTime;

  let canvas;
  let canvasContext;
  let width;
  let height;
  if (canvasId) {
    const canvas = document.getElementById(canvasId || 'audio-meter');
    canvasContext = canvas.getContext('2d');
    width = canvas.clientWidth;
    height = canvas.height;
  }


  function start(streamToShow, force) {
    stream = streamToShow;
    const source = audioCtx.createMediaStreamSource(stream);
    const gainNode = audioCtx.createGain();
    if (force) {
      shouldStop = false;
    }
    source.connect(gainNode);
    gainNode.connect(analyser);
    audioCtx.resume(); // Work around Safari bug where the AudioContext may be suspended a page refresh
    display();
  }

  function stop() {
    shouldStop = true;
    if (canvasId) {
      canvasContext.clearRect(0, 0, width, height);
    }
    stopStream(stream);
    if (audioCtx) {
      audioCtx.close();
    }
  }

  function display() {
    if (type === 'sine_wave') {
      analyser.fftSize = SINE_WAVE_FFT_SIZE;
      const bufferLength = analyser.fftSize;
      const dataArray = new Uint8Array(bufferLength);

      if (canvasId) {
        canvasContext.clearRect(0, 0, width, height);
      }

      const draw = function() {
        if (!shouldStop) {
          setTimeout(draw, 60);

          analyser.getByteTimeDomainData(dataArray);
          onStep(dataArray);

          if (canvasId) {
            canvasContext.fillStyle = 'rgb(200, 200, 200)';
            canvasContext.fillRect(0, 0, width, height);
            canvasContext.lineWidth = 2;
            canvasContext.strokeStyle = 'rgb(0, 0, 0)';
            canvasContext.beginPath();

            const sliceWidth = width * 1.0 / bufferLength;
            let x = 0;

            for (let i = 0; i < bufferLength; i++) {
              const v = dataArray[i] / 128.0;
              const y = v * height / 2;

              if (i === 0) {
                canvasContext.moveTo(x, y);
              } else {
                canvasContext.lineTo(x, y);
              }

              x += sliceWidth;
            }

            canvasContext.lineTo(canvas.width, canvas.height / 2);
            canvasContext.stroke();
          }
        }
      };
      draw();
    } else if (type === 'frequency_bars') {
      analyser.fftSize = BARS_FFT_SIZE;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      if (canvasId) {
        canvasContext.clearRect(0, 0, width, height);
      }

      const barWidth = Math.round((width / bufferLength) * 1.5);

      const draw = function() {
        if (!shouldStop) {
          setTimeout(draw, 60);

          analyser.getByteFrequencyData(dataArray);
          onStep(dataArray);

          if (canvasId) {
            canvasContext.fillStyle = 'rgb(255, 255, 255)';
            canvasContext.fillRect(0, 0, width, height);

            let x = 0;
            for (let i = 0; x < width; i++) {
              const barHeight = dataArray[i];
              canvasContext.fillStyle = `rgba(${r}, ${g}, ${b}, ${barHeight / 100})`;
              canvasContext.fillRect(x, height - barHeight / 2, barWidth, barHeight / 2);

              x += barWidth + 1;
            }
          }
        }
      };
      draw();
    }
  }

  function setVisualType(newType) {
    type = newType;
  }

  return {
    start,
    stop,
    setVisualType,
  };
};
